/**
 * Approval Thresholds Management Page
 * Manage approval levels based on reimbursement claim amounts
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { reimbursementApi } from '../api/reimbursementApi'
import adminApi from '@/modules/admin/api/adminApi'
import type { ApprovalThreshold, CreateApprovalThresholdInput, UpdateApprovalThresholdInput } from '../types/reimbursement.types'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

interface FormData {
  name: string
  description: string
  min_amount: number
  max_amount: number | undefined
  approver_ids: string[]
  level: number
  is_active: boolean
}

const ApprovalThresholdsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)

  // Check if user is admin
  const isAdmin = user?.roles?.some(role => role.name === 'admin')

  // Redirect non-admin users to main page
  useEffect(() => {
    if (user && !isAdmin) {
      toast.error('You do not have permission to access this page')
      navigate('/')
    }
  }, [user, isAdmin, navigate])
  const [editingThreshold, setEditingThreshold] = useState<ApprovalThreshold | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    min_amount: 0,
    max_amount: undefined,
    approver_ids: [],
    level: 1,
    is_active: true,
  })

  // Fetch approval thresholds
  const { data, isLoading } = useQuery({
    queryKey: ['approvalThresholds'],
    queryFn: () => reimbursementApi.getApprovalThresholds(false),
  })

  // Fetch users for approver selection
  const { data: usersData } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => adminApi.getUsers({ limit: 100 }),
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (input: CreateApprovalThresholdInput) => reimbursementApi.createApprovalThreshold(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvalThresholds'] })
      toast.success('Approval threshold created successfully')
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to create approval threshold')
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApprovalThresholdInput }) =>
      reimbursementApi.updateApprovalThreshold(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvalThresholds'] })
      toast.success('Approval threshold updated successfully')
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to update approval threshold')
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: reimbursementApi.deleteApprovalThreshold,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvalThresholds'] })
      toast.success('Approval threshold deleted successfully')
      setShowDeleteConfirm(null)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to delete approval threshold')
    },
  })

  const closeModal = () => {
    setShowModal(false)
    setEditingThreshold(null)
    setFormData({
      name: '',
      description: '',
      min_amount: 0,
      max_amount: undefined,
      approver_ids: [],
      level: 1,
      is_active: true,
    })
  }

  const openCreateModal = () => {
    setEditingThreshold(null)
    setFormData({
      name: '',
      description: '',
      min_amount: 0,
      max_amount: undefined,
      approver_ids: [],
      level: 1,
      is_active: true,
    })
    setShowModal(true)
  }

  const openEditModal = (threshold: ApprovalThreshold) => {
    setEditingThreshold(threshold)
    setFormData({
      name: threshold.name,
      description: threshold.description || '',
      min_amount: threshold.min_amount,
      max_amount: threshold.max_amount || undefined,
      approver_ids: threshold.approvers.map(a => a.id),
      level: threshold.level,
      is_active: threshold.is_active,
    })
    setShowModal(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.approver_ids.length === 0) {
      toast.error('Please select at least one approver')
      return
    }

    const submitData: CreateApprovalThresholdInput = {
      name: formData.name,
      description: formData.description || undefined,
      min_amount: formData.min_amount,
      max_amount: formData.max_amount,
      approver_ids: formData.approver_ids,
      level: formData.level,
      is_active: formData.is_active,
    }

    if (editingThreshold) {
      updateMutation.mutate({ id: editingThreshold.id, data: submitData })
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: (e.target as HTMLInputElement).checked })
    } else if (type === 'number') {
      const numValue = parseFloat(value)
      setFormData({ ...formData, [name]: isNaN(numValue) ? undefined : numValue })
    } else {
      setFormData({ ...formData, [name]: value })
    }
  }

  const toggleApprover = (userId: string) => {
    if (formData.approver_ids.includes(userId)) {
      setFormData({
        ...formData,
        approver_ids: formData.approver_ids.filter(id => id !== userId),
      })
    } else {
      setFormData({
        ...formData,
        approver_ids: [...formData.approver_ids, userId],
      })
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // Show loading while checking auth or redirecting
  if (!user || !isAdmin || isLoading) return <LoadingPage />

  const thresholds = data?.data || []
  const users = usersData?.data || []

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Link to="/reimbursement" className="hover:text-red-600">Reimbursement</Link>
            <span>/</span>
            <span>Approval Thresholds</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Approval Thresholds</h1>
          <p className="text-gray-600 mt-1">Configure approval levels based on claim amounts</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Threshold
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className="font-medium text-red-900">How Approval Thresholds Work</h3>
            <p className="text-sm text-red-700 mt-1">
              When a reimbursement claim is submitted, the system determines the required approver based on the total claim amount.
              Configure thresholds with non-overlapping amount ranges to ensure proper routing.
            </p>
          </div>
        </div>
      </div>

      {/* Thresholds Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount Range</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approvers</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {thresholds
              .sort((a, b) => a.level - b.level)
              .map((threshold) => (
              <tr key={threshold.id} className={`hover:bg-gray-50 ${!threshold.is_active ? 'bg-gray-50 opacity-60' : ''}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-600 font-semibold">
                    {threshold.level}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{threshold.name}</div>
                  {threshold.description && (
                    <div className="text-sm text-gray-500 truncate max-w-xs">{threshold.description}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm">
                    <span className="font-medium">{formatCurrency(threshold.min_amount)}</span>
                    <span className="text-gray-500 mx-2">-</span>
                    <span className="font-medium">
                      {threshold.max_amount ? formatCurrency(threshold.max_amount) : 'Unlimited'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {threshold.approvers.map((approver) => (
                      <span
                        key={approver.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-rose-100 text-rose-800"
                        title={approver.email}
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        {approver.full_name}
                      </span>
                    ))}
                    {threshold.approvers.length === 0 && (
                      <span className="text-gray-400 text-sm italic">No approvers assigned</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    threshold.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {threshold.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEditModal(threshold)}
                      className="text-red-600 hover:text-red-800"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(threshold.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {thresholds.length === 0 && (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No approval thresholds configured</h3>
            <p className="text-gray-500 mb-4">Set up approval levels based on claim amounts.</p>
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Add First Threshold
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingThreshold ? 'Edit Approval Threshold' : 'Create Approval Threshold'}
            </h3>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="e.g., Level 1 - Finance Manager"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Optional description"
                  />
                </div>

                {/* Amount Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Amount (IDR) *
                    </label>
                    <input
                      type="number"
                      name="min_amount"
                      value={formData.min_amount}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Amount (IDR)
                    </label>
                    <input
                      type="number"
                      name="max_amount"
                      value={formData.max_amount || ''}
                      onChange={handleInputChange}
                      min="0"
                      step="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Leave empty for unlimited"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited (highest tier)</p>
                  </div>
                </div>

                {/* Approvers Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approvers * <span className="text-gray-400 font-normal">({formData.approver_ids.length} selected)</span>
                  </label>
                  <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                    {users.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        Loading users...
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {users.map((user) => (
                          <label
                            key={user.id}
                            className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 ${
                              formData.approver_ids.includes(user.id) ? 'bg-red-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.approver_ids.includes(user.id)}
                              onChange={() => toggleApprover(user.id)}
                              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">
                                {user.full_name}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {user.email} {user.job_title && `• ${user.job_title}`}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Select one or more users who can approve claims in this range</p>
                </div>

                {/* Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Level *
                  </label>
                  <input
                    type="number"
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    required
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Used for ordering (1 = lowest level approval)</p>
                </div>

                {/* Active Toggle */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Active</span>
                    <p className="text-xs text-gray-500">Enable this approval threshold</p>
                  </div>
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingThreshold
                    ? 'Update Threshold'
                    : 'Create Threshold'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Approval Threshold</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this approval threshold? This may affect how future claims are routed for approval.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(showDeleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ApprovalThresholdsPage
